import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output } from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { SelectModule } from 'primeng/select';
import { NrSingleselectAieaPropDirective } from './directives/nr-singleselect-aiea-prop.directive';
import { INRSelect, INRSelectChange } from '../../interfaces/nr-select.interface';
import { TooltipModule } from 'primeng/tooltip';

@Component({
  selector: 'nr-singleselect',
  templateUrl: './nr-singleselect.component.html',
  standalone: true,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    CommonModule,
    SelectModule,
    NrSingleselectAieaPropDirective,
    TooltipModule,
  ],
})
export class NRSingleselectComponent {
  @Input() set config(config: INRSelect) {
    this.configData = config;
    this.setDefaultValue();
    this.applyPlaybackValidation();
  }

  @Input() hasError = false;
  @Input() selectHighLight = false;
  @Input() isCatPlaybackActive = false;
  @Input() date: string | null = null;       // <-- add date input
  @Input() duration: string | null = null;   // <-- add duration input
  @Output() selectedDataEvent = new EventEmitter<string>();
  @Input() ariaLabel = 'Dropdown selection';

  defaultConfig: INRSelect = {
    placeholder: 'Select',
    data: [],
    selectedItems: [],
    optionLabel: 'name',
    optionValue: 'id',
    filter: false,
    showToggleAll: false,
    selectionMode: 'checkbox',
    disabled: false,
    isRequiredField: true,
    group: false,
    optionGroupLabel: 'name',
    optionGroupChildren: 'routes',
    ariaLabel: 'Single Select Dropdown',
  };

  configData!: INRSelect;
  isDisabledField = false;
  isRequiredField = false;

  singleSelectFormControl = new FormControl('');

  onChangeHandler(e: INRSelectChange) {
    this.selectedDataEvent.emit(e.value);
  }

  private setDefaultValue() {
    this.configData.label = this.configData.label ?? this.defaultConfig.label;
    this.configData.placeholder = this.configData.placeholder ?? this.defaultConfig.placeholder;
    this.configData.selectedItems =
      this.configData.selectedItems ?? this.defaultConfig.selectedItems;
    this.configData.optionLabel = this.configData.optionLabel ?? this.defaultConfig.optionLabel;
    this.configData.optionValue = this.configData.optionValue ?? this.defaultConfig.optionValue;
    this.isDisabledField = (this.configData.disabled ?? this.defaultConfig.disabled) as boolean;
    this.isRequiredField = (this.configData.isRequiredField ??
      this.defaultConfig.isRequiredField) as boolean;
    this.singleSelectFormControl = this.configData.frmControl ?? this.singleSelectFormControl;
    this.configData.ariaLabel = this.configData.ariaLabel ?? this.defaultConfig.ariaLabel;
  }

  /** 🔑 New validation logic */
  private applyPlaybackValidation() {
    if (!this.isCatPlaybackActive && this.date && this.duration) {
      this.isRequiredField = true;
    }
  }

  get tooltipText(): string | null {
    if (!this.configData?.showTooltip) return null;

    const selected = this.configData.selectedItems;
    const labelKey = this.configData.optionLabel || 'value';
    const placeholder = this.configData.placeholder || '';

    if (!selected || (Array.isArray(selected) && selected.length === 0)) {
      return '';
    }

    if (Array.isArray(selected)) {
      const allPrimitives = selected.every((item) => typeof item !== 'object');
      if (allPrimitives) {
        return placeholder;
      }

      const labels = selected
        .map((item) => this.extractLabel(item, labelKey))
        .filter(Boolean)
        .join(', ');
      return labels || placeholder;
    }

    if (typeof selected !== 'object') {
      return placeholder;
    }

    const label = this.extractLabel(selected, labelKey);
    return label || placeholder;
  }

  private extractLabel(item: unknown, labelKey: string): string {
    if (item === undefined || item === null) return '';

    if (
      typeof item === 'object' &&
      item !== null &&
      labelKey in item &&
      typeof (item as Record<string, unknown>)[labelKey] === 'string'
    ) {
      return (item as Record<string, string>)[labelKey];
    }

    return item?.toString?.() ?? ''; 
  }
}

